import React, { useContext, useState, useEffect } from 'react';
import { Button, DialogActions, Grid, TextField, MenuItem, IconButton } from '@mui/material';
import styles from './styles.module.css';
import CloseIcon from '@mui/icons-material/Close';
import ExpenseService from 'services/ExpenseService';
import VendorServices from 'services/VendorServices';
import ShopSettingsService from 'services/ShopSettingsService';
import { AlertMessagesContext } from 'react-alert-messages';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import Loader from 'components/utils/Loading';
import { ShopContext } from 'Context/ShopContext';
import ShopsHelper from 'helpers/ShopsHelper';
import NumberField from 'components/library/NumberField';
import { useTranslation } from 'react-i18next';

export default function ExpenseFormDialog({ handleClose, handleSuccess, expense, expenseTypes }) {
  const { shop } = useContext(ShopContext);
  const { t: tCommon } = useTranslation('common');
  const { t: tPopup } = useTranslation('popup');

  const getDataToPopulate = () => {
    const populatedData = expense ? { ...expense } : {};
    if (!populatedData.date) {
      populatedData.date = DateTimeHelpers.getTodayStrInYMD();
    } else {
      populatedData.date = DateTimeHelpers.getDateYMD(new Date(populatedData.date));
    }

    if (populatedData.type && populatedData.type.id) {
      populatedData.type_id = populatedData.type.id;
    }

    if (populatedData.account_head && populatedData.account_head.id) {
      populatedData.account_head_id = populatedData.account_head.id;
    }

    if (populatedData.vendor && populatedData.vendor.id) {
      populatedData.vendor_id = populatedData.vendor.id;
    }

    return populatedData;
  };

  const { postAlertMessage } = useContext(AlertMessagesContext);
  const [data, setData] = useState(getDataToPopulate());
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [vendors, setVendors] = useState([]);

  const [paymentModes, setPaymentModes] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [venRes, pmRes] = await Promise.all([
          VendorServices.getVendors(),
          ShopSettingsService.getPaymentModes(),
        ]);
        setVendors(venRes);
        setPaymentModes((pmRes || []).map((mode) => ({ mode_id: mode.id, name: mode.name, amount: 0 })));
      } catch (e) {
        console.error("Failed to load options", e);
      }
    };
    fetchData();
  }, []);

  const handleActionButton = async () => {
    setLoadingIndicator(true);
    try {
      let finalDateStr;
      if (expense && DateTimeHelpers.getDateYMD(new Date(expense.date)) === data.date) {
        finalDateStr = expense.date;
      } else if (data.date === DateTimeHelpers.getTodayStrInYMD()) {
        finalDateStr = new Date().toISOString();
      } else {
        finalDateStr = DateTimeHelpers.startOfDay(data.date);
      }

      const tenders = paymentModes
        .filter(pm => pm.amount > 0)
        .map(pm => ({
          payment_mode: { id: pm.mode_id, mode_id: pm.mode_id },
          amount: pm.amount
        }));

      const payload = {
        ...data,
        date: finalDateStr,
        tenders: tenders
      };

      if (payload.id) {
        await ExpenseService.updateExpense(payload.id, payload);
        postAlertMessage({
          text: tPopup('Expense updated successfully'),
          type: 'success',
        });
      } else {
        await ExpenseService.createExpense(payload);
        postAlertMessage({
          text: tPopup('Expense created successfully'),
          type: 'success',
        });
      }
      handleSuccess();
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    handleClose();
    setLoadingIndicator(false);
  };

  return (
    <div className={styles.popUp}>
      <div className={styles.contentWrapper}>
        <Loader isOpen={loadingIndicator} />
        <div className={styles.headSec}>
          <h2 className={styles.editTitle}>{tCommon('Add a new expense')}</h2>
          <IconButton onClick={handleClose}><CloseIcon /></IconButton>
        </div>
        <div className={styles.inputSec}>
          <Grid container className={styles.inputRow} alignItems="center">
            <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
              <h3 className={styles.inputLabel}>{tCommon('Date')}</h3>
            </Grid>
            <Grid item xs={8} sm={8}>
              <TextField
                label={tCommon('Date')}
                variant="outlined"
                size="small"
                fullWidth
                type="date"
                value={data.date}
                onChange={(e) => {
                  setData({ ...data, date: e.target.value });
                }}
              />
            </Grid>
          </Grid>
          <Grid container className={styles.inputRow} alignItems="center">
            <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
              <h3 className={styles.inputLabel}>{tCommon('Amount')}</h3>
            </Grid>
            <Grid item xs={8} sm={8}>
              <NumberField
                label={tCommon('Amount')}
                value={data.amount || ''}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => {
                  const raw = parseFloat(e.target.value) || 0;
                  const formatted = ShopsHelper.getAmountFormatted(shop, raw);
                  setData({ ...data, amount: formatted });
                }}
              />
            </Grid>
          </Grid>

          <Grid container className={styles.inputRow} alignItems="center">
            <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
              <h3 className={styles.inputLabel}>{tCommon('Expense Type')}</h3>
            </Grid>
            <Grid item xs={8} sm={8}>
              <TextField
                select
                label={tCommon('Expense Type')}
                variant="outlined"
                size="small"
                fullWidth
                SelectProps={{
                  MenuProps: {
                    getContentAnchorEl: null,
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    PaperProps: {
                      style: {
                        maxHeight: 250,
                      },
                    },
                  },
                }}
                value={data.type_id || ''}
                onChange={(e) => {
                  setData({ ...data, type_id: e.target.value });
                }}
              >
                <MenuItem value="">{tCommon('None')}</MenuItem>
                {expenseTypes?.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Grid container className={styles.inputRow} alignItems="center">
            <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
              <h3 className={styles.inputLabel}>{tCommon('Description')}</h3>
            </Grid>
            <Grid item xs={8} sm={8}>
              <TextField
                label={tCommon('Description')}
                variant="outlined"
                size="small"
                fullWidth
                className={styles.numberInput}
                value={data.description}
                onChange={(e) => {
                  setData({ ...data, description: e.target.value });
                }}
              />
            </Grid>
          </Grid>

          <Grid container className={styles.inputRow} alignItems="center" style={{ marginTop: '16px' }}>
            <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
              <h3 className={styles.inputLabel}>{tCommon('Vendor')}</h3>
            </Grid>
            <Grid item xs={8} sm={8}>
              <TextField
                select
                label={tCommon('Vendor')}
                variant="outlined"
                size="small"
                fullWidth
                SelectProps={{
                  MenuProps: {
                    getContentAnchorEl: null,
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    PaperProps: {
                      style: {
                        maxHeight: 250,
                      },
                    },
                  },
                }}
                value={data.vendor_id || ''}
                onChange={(e) => setData({ ...data, vendor_id: e.target.value })}
              >
                <MenuItem value="">{tCommon('None')}</MenuItem>
                {vendors?.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    {v.name}
                  </MenuItem>
                ))}
              </TextField>
              {data.vendor_id && vendors?.find(v => v.id === data.vendor_id) && (() => {
                const vendor = vendors.find(v => v.id === data.vendor_id);
                const currentBalance = vendor.balance || 0;
                const expenseAmount = Number(data.amount) || 0;
                const paidAmount = paymentModes.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);
                const pendingAmount = expenseAmount - paidAmount;
                const updatedBalance = currentBalance - expenseAmount + paidAmount;
                return (
                  <table style={{ fontSize: '14px', marginTop: '12px', marginLeft: '8px', borderSpacing: 0, borderCollapse: 'collapse' }}>
                    <tbody>
                      <tr>
                        <td style={{ color: '#64748b', paddingBottom: '4px' }}>{tCommon('Current Balance')}</td>
                        <td style={{ color: '#64748b', paddingBottom: '4px', paddingLeft: '8px' }}>:</td>
                        <td style={{ color: '#64748b', paddingBottom: '4px', paddingLeft: '8px', textAlign: 'right', fontWeight: 500 }}>{ShopsHelper.getAmountFormatted(shop, currentBalance)}</td>
                      </tr>
                      <tr>
                        <td style={{ color: pendingAmount > 0 ? '#f57c00' : '#64748b', paddingBottom: '4px' }}>{tCommon('Pending Amount')}</td>
                        <td style={{ color: pendingAmount > 0 ? '#f57c00' : '#64748b', paddingBottom: '4px', paddingLeft: '8px' }}>:</td>
                        <td style={{ color: pendingAmount > 0 ? '#f57c00' : '#64748b', paddingBottom: '4px', paddingLeft: '8px', textAlign: 'right', fontWeight: 500 }}>{ShopsHelper.getAmountFormatted(shop, pendingAmount)}</td>
                      </tr>
                      <tr>
                        <td style={{ color: updatedBalance < 0 ? '#d32f2f' : '#2e7d32' }}>{tCommon('Updated Balance')}</td>
                        <td style={{ color: updatedBalance < 0 ? '#d32f2f' : '#2e7d32', paddingLeft: '8px' }}>:</td>
                        <td style={{ color: updatedBalance < 0 ? '#d32f2f' : '#2e7d32', paddingLeft: '8px', textAlign: 'right', fontWeight: 500 }}>{ShopsHelper.getAmountFormatted(shop, updatedBalance)}</td>
                      </tr>
                    </tbody>
                  </table>
                );
              })()}
            </Grid>
          </Grid>

          
          <div style={{ marginTop: 32 }}>
            <h3 className={styles.subTitle} style={{ marginBottom: 24 }}>
              {tCommon('Payment Information')}
            </h3>
            <div style={{ display: 'flex', overflowX: 'auto', gap: '16px', paddingBottom: '8px', paddingTop: '12px', flexWrap: 'nowrap' }}>
              {paymentModes.map((pm) => (
                <div key={pm.mode_id} style={{ minWidth: '120px', flexShrink: 0 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                    <NumberField
                      label={pm.name}
                      value={pm.amount || ''}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      fullWidth
                      onChange={(e) => {
                        const updatedAmount = Number(e.target.value);
                        setPaymentModes((prev) => {
                          return prev.map((item) =>
                            item.mode_id === pm.mode_id ? { ...item, amount: updatedAmount } : item
                          );
                        });
                      }}
                    />
                    <Button
                      size="small"
                      variant="text"
                      color="primary"
                      title={tCommon('Pay Full')}
                      style={{ fontSize: '10px', textTransform: 'none', padding: '2px 4px', minWidth: 'auto', lineHeight: 1 }}
                      onClick={() => {
                        const payableAmount = Number(data.amount) || 0;
                        setPaymentModes((prev) => {
                          return prev.map((item) =>
                            item.mode_id === pm.mode_id
                              ? { ...item, amount: payableAmount }
                              : { ...item, amount: 0 }
                          );
                        });
                      }}
                    >
                      {tCommon('Payment In Full')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className={styles.footSec}>
          <DialogActions>
            <Button variant="contained" color="secondary" onClick={handleClose} className={styles.closeBtn}>
              {tCommon('Close')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              style={{ backgroundColor: '#00a65a' }}
              disabled={
                !data.date ||
                data.date === '' ||
                !data.amount ||
                data.amount === ''
              }
              onClick={handleActionButton}
            >
              {data.id ? tCommon('Update') : tCommon('Create')}
            </Button>
          </DialogActions>
        </div>
      </div>
    </div>
  );
}
